import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ASSIGNMENT_FILTERS,
  type AssignmentFilter,
  type CardFilter,
  DEADLINE_FILTERS,
  type DeadlineFilter,
  type Label,
  LabelGrid,
  STATUS_FILTERS,
  type StatusFilter,
  toggleSelection,
} from '@/entities/board';
import { Button, HelpTip, Icons, Popover } from '@/shared/ui';

import styles from './BoardFilterBar.module.css';
import { type FilterOption, OptionFilter } from './OptionFilter';

interface Props {
  labels: Label[];
  filter: CardFilter;
  onChange: (update: (current: CardFilter) => CardFilter) => void;
}

interface SelectProps extends Omit<Props, 'labels'> {
  variant?: 'popover' | 'inline';
}

/**
 * Status, deadline and assignment as multi-select categories: shared by the inline desktop bar
 * (popovers) and the phone filter sheet (inline groups).
 */
export function SelectFilters({ filter, onChange, variant = 'popover' }: SelectProps) {
  const { t } = useTranslation();
  const status: FilterOption<StatusFilter>[] = [
    { value: 'open', label: t('filter.statusOpen') },
    { value: 'done', label: t('filter.statusDone') },
  ];
  const deadline: FilterOption<DeadlineFilter>[] = [
    { value: 'has', label: t('filter.deadlineHas') },
    { value: 'none', label: t('filter.deadlineNone') },
    { value: 'overdue', label: t('filter.deadlineOverdue') },
    { value: 'today', label: t('filter.deadlineToday') },
    { value: 'tomorrow', label: t('filter.deadlineTomorrow') },
  ];
  const assignment: FilterOption<AssignmentFilter>[] = [
    { value: 'me', label: t('assignment.filterMine') },
    { value: 'others', label: t('assignment.filterOthers') },
    { value: 'shared', label: t('assignment.filterShared') },
    { value: 'unassigned', label: t('assignment.filterUnassigned') },
  ];
  return (
    <>
      <OptionFilter
        variant={variant}
        label={t('filter.status')}
        options={status}
        selected={filter.status}
        onChange={(next) => {
          onChange((current) => ({ ...current, status: next }));
        }}
        onToggle={(option) => {
          onChange((current) => ({
            ...current,
            status: toggleSelection(current.status, option, STATUS_FILTERS, true),
          }));
        }}
      />
      <OptionFilter
        variant={variant}
        label={t('filter.deadline')}
        options={deadline}
        selected={filter.deadline}
        onChange={(next) => {
          onChange((current) => ({ ...current, deadline: next }));
        }}
        onToggle={(option) => {
          onChange((current) => ({
            ...current,
            deadline: toggleSelection(current.deadline, option, DEADLINE_FILTERS),
          }));
        }}
      />
      <OptionFilter
        variant={variant}
        label={t('assignment.filterTitle')}
        options={assignment}
        selected={filter.assignment}
        onChange={(next) => {
          onChange((current) => ({ ...current, assignment: next }));
        }}
        onToggle={(option) => {
          onChange((current) => ({
            ...current,
            assignment: toggleSelection(current.assignment, option, ASSIGNMENT_FILTERS),
          }));
        }}
      />
    </>
  );
}

function toggleLabel(id: string) {
  return (current: CardFilter): CardFilter => ({
    ...current,
    labelIds: current.labelIds.includes(id)
      ? current.labelIds.filter((x) => x !== id)
      : [...current.labelIds, id],
  });
}

/** The label chooser itself (grid + match mode + clear): used in the dropdown and in the phone sheet. */
export function LabelFilters({ labels, filter, onChange }: Props) {
  const { t } = useTranslation();
  const toggle = useCallback(
    (id: string) => {
      onChange(toggleLabel(id));
    },
    [onChange],
  );
  const count = filter.labelIds.length;
  return (
    <div className={styles.labelPanel}>
      <LabelGrid labels={labels} selectedIds={filter.labelIds} onToggle={toggle} />
      <div className={styles.labelFooter}>
        <fieldset className={styles.matchMode} disabled={count < 2}>
          <legend className="visually-hidden">{t('filter.matchMode')}</legend>
          {(['any', 'all'] as const).map((mode) => (
            <label key={mode} className={styles.matchOption}>
              <input
                type="radio"
                name="label-match"
                checked={filter.labelMatch === mode}
                onChange={() => {
                  onChange((current) => ({ ...current, labelMatch: mode }));
                }}
              />
              {mode === 'any' ? t('filter.matchAny') : t('filter.matchAll')}
            </label>
          ))}
        </fieldset>
        <HelpTip
          topic={t('filter.matchMode')}
          topicId="board-filters"
          text={t('help.labelFilter')}
        />
        <span className={styles.selectedCount}>{t('filter.labelsSelected', { count })}</span>
        <Button
          size="sm"
          variant="ghost"
          disabled={count === 0}
          onClick={() => {
            onChange((current) => ({ ...current, labelIds: [], labelMatch: 'any' }));
          }}
        >
          {t('filter.labelsClear')}
        </Button>
      </div>
    </div>
  );
}

/**
 * ONE compact control instead of every label on the toolbar: "Labels · 3" opens a popover with a
 * searchable multi-column grid. 100 labels cost one button, not a wall of chips.
 */
export function LabelFilterDropdown({ labels, filter, onChange }: Props) {
  const { t } = useTranslation();
  if (labels.length === 0) return null;
  const count = filter.labelIds.length;
  return (
    <Popover
      label={t('filter.labelsPanel')}
      width={560}
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={`${styles.labelsButton} ${count > 0 ? styles.labelsButtonOn : ''}`}
          data-open={open ? 'true' : undefined}
        >
          <Icons.tag />
          {count > 0 ? t('filter.labelsButtonCount', { count }) : t('filter.labelsButton')}
        </button>
      )}
    >
      <div className={styles.labelPopover}>
        <LabelFilters labels={labels} filter={filter} onChange={onChange} />
      </div>
    </Popover>
  );
}
